import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
  generateWeeksForMonth,
  getOccupancy,
  getColorForOccupancy,
  formatDateToYMDString,
} from './helper';
import { formatDate } from 'src/utils/functions';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';

interface CalendarHeatmapProps {
  year: number;
  month: number;
  occupancyData: Record<string, number>;
  monthTitle?: React.ReactNode;
  secondaryData?: Record<string, number>;
  calendarType?: 'historical' | 'future';
  showStatus?: boolean;
}

const CalendarHeatmap: React.FC<CalendarHeatmapProps> = ({
  year,
  month,
  occupancyData,
  monthTitle,
  secondaryData = {},
  calendarType = 'historical',
  showStatus = false,
}) => {
  const currentMonthDate = new Date(year, month, 1);
  const weeks = generateWeeksForMonth(year, month);

  return (
    <Box>
      {/* Month and Year */}
      {monthTitle || (
        <Typography variant="body1" className="subHeadings" sx={{ mb: 1 }}>
          {currentMonthDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </Typography>
      )}

      {/* Week Header */}
      <Grid container columns={7} spacing={0.5} sx={{ mb: 0.5 }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dow, dIndex) => (
          <Grid item xs={1} key={dIndex + 1}>
            <Typography variant="body2" align="center">
              {dow}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Render Each Week */}
      {weeks.map((week, wIndex) => (
        <Grid key={wIndex} container columns={7} spacing={0.5} sx={{ mb: 0.5 }}>
          {week.map((day, dIndex) => {
            const isCurrentMonth = day.getFullYear() === year && day.getMonth() === month;
            if (!isCurrentMonth) {
              return <Grid item xs={1} key={dIndex}></Grid>;
            }
            const dateStr = formatDateToYMDString(day);
            const hasData = occupancyData[dateStr] !== undefined;
            const hasSecondary = secondaryData[dateStr] !== undefined;
            const isFuture = day > new Date();
            let tooltipContent: React.ReactNode = null;
            let occupancyValue = 0;
            let bgColor = '#EEE';
            if (hasData) {
              occupancyValue = getOccupancy(dateStr, occupancyData);
              bgColor = getColorForOccupancy(occupancyValue);
              tooltipContent = showStatus ? (
                <span>{occupancyValue == 100 ? 'Booked' : 'Available'}</span>
              ) : (
                <span>
                  Occupancy Rate: <span style={{ fontWeight: 'bold' }}>{occupancyValue}%</span>
                </span>
              );
            } else {
              if (calendarType === 'historical' && hasSecondary) {
                tooltipContent = <span>Preliminary data may be available for recent dates</span>;
              } else if (calendarType === 'future' && hasSecondary) {
                tooltipContent = <span>Check Historical Daily Occupancy Calendar above</span>;
              } else if (calendarType === 'future' && isFuture) {
                tooltipContent = <span>Data will be refreshed coming week</span>;
              } else {
                tooltipContent = <span>No data available</span>;
              }
            }
            const dayNum = day.getDate();
            const DateBoxUi = (
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: bgColor,
                  borderRadius: 1,
                  cursor: 'pointer',
                }}
              >
                <Typography variant="caption" color="textPrimary">
                  {dayNum}
                </Typography>
              </Box>
            );
            return (
              <Grid item xs={1} key={dIndex}>
                <TooltipNew
                  title={`${formatDate(dateStr)}`}
                  content={tooltipContent as JSX.Element}
                  arrow={true}
                >
                  {DateBoxUi}
                </TooltipNew>
              </Grid>
            );
          })}
        </Grid>
      ))}
    </Box>
  );
};

export default CalendarHeatmap;
